import { identity, uniqBy } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { LayoutModel } from '@paragon/contract-library';

import { getInsertionsWithPendingChanges } from './changeTracker';
import { getChildrenFinder } from './ChildrenFinder';

export interface QuantityMap {
  [itemGuid: string]: {
    [parentGuid: string]: number;
  };
}

export const getQuantityMap = createSelector([getInsertionsWithPendingChanges], (insertions) =>
  insertions.reduce<QuantityMap>((quantityMap, insertion) => {
    if (!quantityMap[insertion.objectGuid]) quantityMap[insertion.objectGuid] = {};
    if (!quantityMap[insertion.objectGuid][insertion.parentGuid]) {
      quantityMap[insertion.objectGuid][insertion.parentGuid] = 0;
    }
    quantityMap[insertion.objectGuid][insertion.parentGuid] += 1;
    return quantityMap;
  }, {}),
);

export const getLayoutQuantityMap = createSelector([getInsertionsWithPendingChanges], (insertions) =>
  insertions.reduce<QuantityMap>((quantityMap, insertion) => {
    if (!quantityMap[insertion.objectGuid]) quantityMap[insertion.objectGuid] = {};

    if (!quantityMap[insertion.objectGuid][insertion.parentGuid]) {
      quantityMap[insertion.objectGuid][insertion.parentGuid] = 0;
    }

    if (insertion.type === 'LayoutComponent') {
      quantityMap[insertion.objectGuid][insertion.parentGuid] += 1;
    }

    return quantityMap;
  }, {}),
);

export interface QuantitiesWithModels {
  [guid: string]: QuantityWithModel;
}

interface QuantityWithModel {
  readonly model: LayoutModel;
  readonly quantity: number;
}

export const getFlattenedChildrenWithCalculatedQuantity = createSelector(
  getQuantityMap,
  getChildrenFinder,

  (quantityMap: QuantityMap, findChildrenInState: (parentGuid: string) => LayoutModel[]) => (itemGuid: string) => {
    const result: QuantityWithModel[][] = [];
    const firstLevelChildren = uniqBy(findChildrenInState(itemGuid), (x) => x.guid).map((model) => ({
      model,
      parentQuantity: 1,
      parentGuid: itemGuid,
    }));
    const recursiveGetChildrenWithCalculatedQuantities = (
      layoutModelsWithParentInfo: { model: LayoutModel; parentQuantity: number; parentGuid: string }[],
    ) => {
      let nextLevelChildren: { model: LayoutModel; parentQuantity: number; parentGuid: string }[] = [];

      result.push([]);

      for (const currentModelNode of layoutModelsWithParentInfo) {
        const quantity =
          quantityMap[currentModelNode.model.guid][currentModelNode.parentGuid] * currentModelNode.parentQuantity;

        result[result.length - 1].push({ model: currentModelNode.model, quantity });

        nextLevelChildren = nextLevelChildren.concat(
          uniqBy(findChildrenInState(currentModelNode.model.guid), (x) => x.guid).map((model) => ({
            model,
            parentQuantity: quantity,
            parentGuid: currentModelNode.model.guid,
          })),
        );
      }
      if (nextLevelChildren.length > 0) recursiveGetChildrenWithCalculatedQuantities(nextLevelChildren);
    };
    recursiveGetChildrenWithCalculatedQuantities(firstLevelChildren);

    const quantitiesWithModels = result.flatMap(identity);

    // Merge quantities from the same layout models with different parents
    return quantitiesWithModels.reduce<QuantitiesWithModels>(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.model.guid]: {
          ...currentValue,
          quantity: accumulator[currentValue.model.guid]
            ? accumulator[currentValue.model.guid].quantity + currentValue.quantity
            : currentValue.quantity,
        },
      }),
      {},
    );
  },
);

export const getComponentDesignQuantities = (componentDesignQuantitiesWithModels: QuantitiesWithModels) => {
  const componentDesignQuantities: { [guid: string]: number } = {};
  for (const [componentDesignGuid, quantityWithModel] of Object.entries(componentDesignQuantitiesWithModels)) {
    if (quantityWithModel.quantity !== 0 && quantityWithModel.model.modelType === 'ComponentDesign') {
      componentDesignQuantities[componentDesignGuid] = quantityWithModel.quantity;
    }
  }

  return componentDesignQuantities;
};
